import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, mergeMap } from 'rxjs/operators';

import { IpExclusionsService } from '../../../core/services/ip-exclusions.service';
import * as IpExclusionsActions from './ip-exclusions.actions';
import {
  selectIpExclusionsMeta,
  selectIpExclusionsLastQuery,
} from './ip-exclusions.selectors';
import {
  DEFAULT_IP_EXCLUSIONS_CURRENT_PAGE,
  DEFAULT_IP_EXCLUSIONS_PER_PAGE,
} from '../models/ip-exclusion.model';

@Injectable()
export class IpExclusionsEffects {
  private actions$ = inject(Actions);
  private service = inject(IpExclusionsService);
  private store = inject(Store);

  loadIpExclusions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IpExclusionsActions.loadIpExclusions),
      switchMap((action) =>
        this.service
          .getIpExclusionsList({
            page: action.page ?? DEFAULT_IP_EXCLUSIONS_CURRENT_PAGE,
            perPage: action.perPage ?? DEFAULT_IP_EXCLUSIONS_PER_PAGE,
            search: action.search ?? '',
            sort: action.sort ?? 'created_at',
            sortDirection: action.sortDirection ?? 'desc',
            filters: action.filters,
            filterLink: action.filterLink,
          })
          .pipe(
            map((response) =>
              IpExclusionsActions.loadIpExclusionsSuccess({
                ipExclusions: response.data,
                meta: response.meta,
              })
            ),
            catchError((error) =>
              of(
                IpExclusionsActions.loadIpExclusionsFailure({
                  error: error?.message ?? 'Failed to load IP exclusions',
                })
              )
            )
          )
      )
    )
  );

  loadStatistics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IpExclusionsActions.loadIpExclusionStatistics),
      switchMap(() =>
        this.service.getStatistics().pipe(
          map((statistics) =>
            IpExclusionsActions.loadIpExclusionStatisticsSuccess({ statistics })
          ),
          catchError((error) =>
            of(
              IpExclusionsActions.loadIpExclusionStatisticsFailure({
                error: error?.message ?? 'Failed to load statistics',
              })
            )
          )
        )
      )
    )
  );

  submitIpExclusion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IpExclusionsActions.submitIpExclusion),
      switchMap(({ payload, isEditMode }) => {
        const request$ =
          isEditMode && payload.uuid
            ? this.service.updateIpExclusion(payload.uuid, payload)
            : this.service.createIpExclusion(payload);

        return request$.pipe(
          map((result) =>
            result?.duplicate
              ? IpExclusionsActions.submitIpExclusionDuplicate({
                  duplicate: result.duplicate,
                })
              : IpExclusionsActions.submitIpExclusionSuccess()
          ),
          catchError((error) =>
            of(
              IpExclusionsActions.submitIpExclusionFailure({
                error: error?.message ?? 'Failed to save IP exclusion',
              })
            )
          )
        );
      })
    )
  );

  deleteIpExclusion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IpExclusionsActions.deleteIpExclusion),
      switchMap(({ uuid }) =>
        this.service.deleteIpExclusion(uuid).pipe(
          map(() => IpExclusionsActions.deleteIpExclusionSuccess()),
          catchError((error) =>
            of(
              IpExclusionsActions.deleteIpExclusionFailure({
                error: error?.message ?? 'Failed to delete IP exclusion',
              })
            )
          )
        )
      )
    )
  );

  bulkDeleteIpExclusions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IpExclusionsActions.bulkDeleteIpExclusions),
      switchMap(({ uuids, allSelected }) =>
        this.service
          .bulkDeleteIpExclusions({ exclusionIpUuids: uuids, allSelected })
          .pipe(
            map(() => IpExclusionsActions.bulkDeleteIpExclusionsSuccess()),
            catchError((error) =>
              of(
                IpExclusionsActions.bulkDeleteIpExclusionsFailure({
                  error: error?.message ?? 'Failed to delete IP exclusions',
                })
              )
            )
          )
      )
    )
  );

  loadIpExclusionById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IpExclusionsActions.loadIpExclusionById),
      switchMap(({ uuid }) =>
        this.service.getIpExclusionById(uuid).pipe(
          map((ipExclusion) =>
            IpExclusionsActions.loadIpExclusionByIdSuccess({ ipExclusion })
          ),
          catchError((error) =>
            of(
              IpExclusionsActions.loadIpExclusionByIdFailure({
                error: error?.message ?? 'Failed to load IP exclusion',
              })
            )
          )
        )
      )
    )
  );

  importIpExclusions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IpExclusionsActions.importIpExclusions),
      switchMap(({ formData }) =>
        this.service.importCsv(formData).pipe(
          map(() => IpExclusionsActions.importIpExclusionsSuccess()),
          catchError((error) =>
            of(
              IpExclusionsActions.importIpExclusionsFailure({
                error: error?.message ?? 'Failed to import IP exclusions',
              })
            )
          )
        )
      )
    )
  );

  /**
   * After any mutation that changes the list (submit/delete/bulk delete/import),
   * reload both the current page and the statistics using the last query params.
   */
  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        IpExclusionsActions.submitIpExclusionSuccess,
        IpExclusionsActions.deleteIpExclusionSuccess,
        IpExclusionsActions.bulkDeleteIpExclusionsSuccess,
        IpExclusionsActions.importIpExclusionsSuccess
      ),
      withLatestFrom(
        this.store.select(selectIpExclusionsMeta),
        this.store.select(selectIpExclusionsLastQuery)
      ),
      mergeMap(([, meta, lastQuery]) => [
        IpExclusionsActions.loadIpExclusions({
          page: meta?.currentPage ?? DEFAULT_IP_EXCLUSIONS_CURRENT_PAGE,
          perPage: meta?.perPage ?? DEFAULT_IP_EXCLUSIONS_PER_PAGE,
          search: lastQuery.search,
          filters: lastQuery.filters,
          filterLink: lastQuery.filterLink,
          sort: lastQuery.sort,
          sortDirection: lastQuery.sortDirection,
        }),
        IpExclusionsActions.loadIpExclusionStatistics(),
      ])
    )
  );
}
